import * as React from 'react';
import moment from 'moment';
import Select, { MultiValue, OptionProps, SingleValue, components } from 'react-select';
import { Popup } from 'semantic-ui-react';
import { GridPDFExport } from '@progress/kendo-react-pdf';

import 'semantic-ui-css/semantic.min.css';
import Button from 'common/button/button';
import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import DateRangeSelector from 'common/date-range-selector/date-range-selector';
import ScheduleRegModal, { IScheEmpItem } from 'components/commuting/schedule-reg-modal/schedule-reg-modal';
import 'screens/commuting/commuting.scss';

// Assets
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info_icon.svg';

// Data
import CommutingData from './commuting-data.json';
import { AxiosResponse } from 'axios';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import { ICommuteEmpItem, ICommuteItem, ICommuteItemData, ISchDateItem } from 'types/commuting';
import {
  formatKoreanTimeToDateObj,
  formatTimeDigitalToKorean,
  formatTimeNumberToKorean,
  getDayWithKorean,
  onlyNumberInput,
} from 'constants/utils';
import { DAYS } from 'constants/CONSTATNS';
import CommuteGrid from 'components/commuting/CommuteGrid/CommuteGrid';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import { GridRowClickEvent } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import CommonModal from 'common/CommonModal';
import useStoreStore from 'zustands/useStoreStore';
import CommuteModify, { IStateModal } from 'components/commuting/CommuteModify/CommuteModify';
import useUserStore from 'zustands/useUserStore';
import AgGrid from 'components/AgGrid';
import { CellClassParams, ColDef, ColGroupDef, RowClickedEvent } from 'ag-grid-community';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { AgGridReact } from 'ag-grid-react';
import useGridExport from 'hooks/useGridExport';

const Commuting: React.FC = () => {
  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();
  const { storeName, storeSeqNumber } = useStoreStore();
  const { userType, managerInfo } = useUserStore();

  const { webApiNode, webApiPhp } = useApi();
  const { checkErrorLog } = useErrorLog();
  const { exportToPdf } = useGridExport();

  const [startDate, setStartDate] = React.useState<Date>(
    moment(new Date(moment().year(), moment().month(), 1)).toDate(),
  ); // 조회 시작일;
  const [endDate, setEndDate] = React.useState<Date>(moment().toDate()); // 조회 종료일
  const [selectEmpList, setSelectEmpList] = React.useState<ICommuteEmpItem[]>([]); // 직원선택목록
  const [selectedEmp, setSelectedEmp] = React.useState<ICommuteEmpItem | null>(null); // 직원목록에서 선택된 직원
  const [commuteData, setCommuteData] = React.useState<ICommuteItem[]>([]); // 출퇴근기록 테이블 데이터
  const [commuteTotalData, setCommuteTotalData] = React.useState<ICommuteItem[]>([]); // 출퇴근기록 테이블 데이터
  const [commuteExcelData, setCommuteExcelData] = React.useState<ICommuteItem[]>([]); // 출퇴근기록 테이블 데이터
  const [scheduleRegModal, setScheduleRegModal] = React.useState<boolean>(false); // 추가일정 모달 표시여부
  const [visibleCommuteModifyModal, setVisibleCommuteModifyModal] = React.useState<boolean>(false); // 일정수정 모달 표시여부
  const [selectedCommuteRow, setSelectedCommuteRow] = React.useState<ICommuteItemData>(); // 일정 수정 입력폼 데이터

  const colourStyles = {
    control: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '40px',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  // 설정한 기간에 근무한 직원목록 패칭
  const fetchEmpByPeriodData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getschedulesemplist`, {
        START: moment(startDate).format('YYYY-MM-DD'),
      });
      // console.log('/auth/getscheduleemplist_data', data);

      if (data.resultCode === 0) {
        const list: ICommuteEmpItem[] = [{ empSeq: 0, empName: '전직원', retireDate: null }];
        data.resultData.map((i: any) => {
          list.push({
            empSeq: i.EMP_SEQ,
            empName: i.EMP_NAME,
            retireDate: i.END ? moment(i.END).toDate() : i.END,
          });
        });
        setSelectEmpList(list);
        const emp = list.find((i: any) => {
          return i.empSeq === selectedEmp?.empSeq;
        });
        setSelectedEmp(emp ? emp : list.length > 0 ? list[1] : list[0]);
      } else {
        throw new Error('/auth/getscheduleemplist');
      }
    } catch (err) {
      checkErrorLog(err, 'commuting /auth/getscheduleemplist');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 직원목록 선택
  const handleSelectEmp = (
    newValue: SingleValue<{ value: number | undefined; label: string | undefined; retireDate: Date | null }>,
  ) => {
    const emp = selectEmpList.find((i) => i.empSeq === newValue?.value);
    if (emp) setSelectedEmp(emp);
  };

  // 설정한 기간과 직원에 대한 출퇴근 기록 패칭
  const fetchSchedules = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getAllSchedulesweb`, {
        ALL: selectedEmp?.empSeq === 0 ? '1' : '0',
        EMP_SEQ: selectedEmp?.empSeq,
        START: moment(startDate).format('YYYY-MM-DD'), //dateformat3(schState.value.start),
        END: moment(endDate).format('YYYY-MM-DD'), //dateformat3(schState.value.end),
      });
      // console.log('/auth/getAllSchedulesweb_data', data);

      if (data.resultCode === 0) {
        const scheduleData = data.resultData.schedules;
        for (let key in scheduleData) {
          for (let j = 0; j < scheduleData[key].EMP_LIST.length; j++) {
            let {
              MEMBER_SEQ,
              EMP_ID,
              DATE,
              NEW_START,
              NEW_END,
              NEW_CHANGE_END,
              NEW_CHANGE_START,
              NEW_START_TIME,
              NEW_END_TIME,
              NEW_UPDATED_START,
              NEW_UPDATED_END,
              REST_TIME,
              SCH_ID,
            } = scheduleData[key].EMP_LIST[j];
            MEMBER_SEQ = MEMBER_SEQ;
            DATE = moment(DATE).toDate(); // YYYY-MM-D;
            EMP_ID = Number(EMP_ID);
            NEW_START = NEW_START ? moment(NEW_START).toDate() : NEW_START;
            NEW_END = NEW_END ? moment(NEW_END).toDate() : NEW_END;
            NEW_CHANGE_END = NEW_CHANGE_END ? moment(NEW_CHANGE_END).toDate() : NEW_CHANGE_END;
            NEW_CHANGE_START = NEW_CHANGE_START ? moment(NEW_CHANGE_START).toDate() : NEW_CHANGE_START;
            NEW_START_TIME = NEW_START_TIME ? moment(NEW_START_TIME).toDate() : NEW_START_TIME;
            NEW_END_TIME = NEW_END_TIME ? moment(NEW_END_TIME).toDate() : NEW_END_TIME;
            NEW_UPDATED_START = NEW_UPDATED_START ? moment(NEW_UPDATED_START).toDate() : NEW_UPDATED_START;
            NEW_UPDATED_END = NEW_UPDATED_END ? moment(NEW_UPDATED_END).toDate() : NEW_UPDATED_END;
            REST_TIME = Number(REST_TIME);
            SCH_ID = Number(SCH_ID ?? 0);
          }
        }
        gridDataFormat(scheduleData);
      } else {
        throw new Error('/auth/getAllSchedulesweb');
      }
    } catch (err) {
      checkErrorLog(err, 'commuting /auth/getAllSchedulesweb');
    } finally {
    }
  };

  // 출퇴근기록 가공
  const gridDataFormat = (schData: { [key: string]: { EMP_LIST: ICommuteItemData[] } }) => {
    // console.log('gridDataFormat_schData', schData);
    let prevData = schData;
    let formatData = [];
    let totalFormat: any[] = [{}];

    for (let key in prevData) {
      if (prevData[key].EMP_LIST.length !== 0) {
        let today: any = new Date();
        prevData[key].EMP_LIST.map((item, index) => {
          let DATE = null;
          let WEEKEND: DAYS = '월';
          let NAME = null;
          let NEW_START = null;
          let NEW_END = null;
          let NEW_CHANGE_START = null;
          let NEW_CHANGE_END = null;
          let NEW_START_TIME = null;
          let NEW_END_TIME = null;
          let NEW_UPDATED_START = null;
          let NEW_UPDATED_END = null;
          let WORK_TIME = null;
          let REST_TIME = null;
          let LATE_TIME = null;
          let EARLYLEAVE_TIME = null;
          let REAL_WORKTIME = null;
          let PLUSTIME = null;
          let NIGHTTIME = null;
          let SCH_STATUS = null;
          let SCH_TYPE = null;
          let SCH_ID = null;
          let EMP_ID = null;
          let WORK_ON_MODEL = null;
          let WORK_OFF_MODEL = null;

          DATE = moment(key).toDate();
          let workDate = new Date(key); // 근무일자
          WEEKEND = getDayWithKorean(new Date(key));
          NAME = item.EMP_NAME ? decodeURI(item.EMP_NAME) : decodeURI(item.NAME);
          if (item.VACATION !== '1') {
            NEW_START = item.NEW_START
              ? moment(item.NEW_START).format('HH시mm분')
              : formatTimeDigitalToKorean(item.ATTENDANCE_TIME ?? '');
            NEW_END = item.NEW_END
              ? moment(item.NEW_END).format('HH시mm분')
              : formatTimeDigitalToKorean(item.WORK_OFF_TIME ?? '');
            if (item.NEW_CHANGE_START && moment(item.NEW_CHANGE_START).isValid())
              NEW_CHANGE_START = moment(item.NEW_CHANGE_START).format('HH시mm분'); //일정수정출근
            if (item.NEW_CHANGE_END && moment(item.NEW_CHANGE_END).isValid())
              NEW_CHANGE_END = moment(item.NEW_CHANGE_END).format('HH시mm분'); //일정수정퇴근
            if (item.NEW_START_TIME && moment(item.NEW_START_TIME).isValid())
              NEW_START_TIME = moment(item.NEW_START_TIME).format('HH시mm분'); //qr출근
            if (item.NEW_END_TIME && moment(item.NEW_END_TIME).isValid())
              NEW_END_TIME = moment(item.NEW_END_TIME).format('HH시mm분'); //qr퇴근
            if (item.NEW_UPDATED_START && moment(item.NEW_UPDATED_START).isValid())
              NEW_UPDATED_START = moment(item.NEW_UPDATED_START).format('HH시mm분'); //qr수정출근
            if (item.NEW_UPDATED_END && moment(item.NEW_UPDATED_END).isValid())
              NEW_UPDATED_END = moment(item.NEW_UPDATED_END).format('HH시mm분'); //qr수정퇴근
            REST_TIME = item.REST_TIME; // 휴게시간
            if (item.NEW_CHANGE_START) {
              workDate = new Date(item.NEW_CHANGE_START);
            }
            if (item.NEW_START) {
              workDate = new Date(item.NEW_START);
            }

            if (item.AUTOWORKOFF == '1') {
              NEW_END_TIME = '퇴근미체크';
              if (item.TYPE == '4') {
                NEW_END = '퇴근미체크';
              }
            }

            // 예정근무시간
            let S: Date | string | null = item.NEW_UPDATED_START ?? null; // qr수정출근
            if (!S) S = moment(item.NEW_START_TIME).toDate(); // qr출근
            let E: Date | string | null = item.NEW_UPDATED_END ?? null; // qr수정퇴근
            if (!E) E = moment(item.NEW_END_TIME).toDate(); // qr퇴근
            let BS: Date | string | null = item.NEW_CHANGE_START ?? null; // 일정수정출근
            if (!BS) BS = moment(item.NEW_START).toDate(); // 일정출근
            if (BS == null) {
              BS = moment(moment(item.DATE).format('YYYY-MM-DD') + ' ' + item.ATTENDANCE_TIME).toDate(); // 일정출근
            }
            let BE: Date | string | null = item.NEW_CHANGE_END ?? null; //일정수정퇴근
            if (!BE) BE = moment(item.NEW_END).toDate(); // 일정퇴근
            if (BE == null) {
              BE = moment(moment(item.DATE).format('YYYY-MM-DD') + ' ' + item.WORK_OFF_TIME).toDate(); // 일정퇴근
            }

            // console.log('S', S);
            // console.log('BS', BS);
            BS = new Date(BS);
            BE = new Date(BE);
            S = new Date(S);
            E = new Date(E);
            if (S < BS) {
              // 출근시간이 일정출근보다 클 때 일정시간으로 최소화
              S = BS;
            }
            if (E == null && S != null) {
              // 퇴근기록없이 출근기록만 있을 경우 퇴근시간을 일정퇴근시간으로 최소화
              E = BE;
            }
            if (E > BE) {
              // 퇴근기록이 일정퇴근기록보다 클 경우 일정퇴근시간으로 최소화
              E = BE;
            }
            let workTime: number | null = Math.floor(BE.getTime() - BS.getTime()) / (60 * 1000);
            if (!workTime || workTime <= 0) {
              workTime = null;
            }
            WORK_TIME = workTime;

            let restTime: number | null = Math.floor(Number(item.REST_TIME));
            if (restTime <= 0) {
              restTime = null;
            }
            REST_TIME = restTime;

            let B: string | Date | null = item.NEW_UPDATED_START ?? null; // 실제출근
            if (B == null) {
              B = item.NEW_START_TIME ?? null;
            }
            let G: string | Date | null = item.NEW_CHANGE_START ?? null; // 일정출근
            if (G == null) {
              G = item.NEW_START ?? null;
            }
            let lateTime;
            if (G == null || B == null) {
              // 일정 없거나 출근기록 하나라도 없는 경우 0
              lateTime = 0;
            } else {
              B = new Date(B);
              G = new Date(G);
              lateTime = Math.floor(B.getTime() - G.getTime()) / (60 * 1000);
              if (!lateTime || lateTime <= 0) {
                lateTime = 0;
              }
            }
            LATE_TIME = lateTime;

            // 조퇴시간
            if ((item.NEW_START_TIME || item.NEW_UPDATED_START) && (item.NEW_UPDATED_END || item.NEW_END_TIME)) {
              // 일정과 실제 출근 모두 있을 때
              let E: string | Date | null = item.NEW_UPDATED_END ?? null; // 퇴근시간
              if (E == null) {
                E = item.NEW_END_TIME ?? null;
              }
              let G: string | Date | null = item.NEW_CHANGE_END ?? null; // 일정퇴근
              if (G == null) {
                G = item.NEW_END ?? null;
              }
              E = new Date(E ?? new Date());
              G = new Date(G ?? new Date());
              let EearlyleaveTime = Math.floor(G.getTime() - E.getTime()) / (60 * 1000); // 일정퇴근 - 실제 퇴근
              if (EearlyleaveTime <= 0) {
                // 일정퇴근보다 실제 퇴근이 클경우 0
                EARLYLEAVE_TIME = 0;
              } else {
                EARLYLEAVE_TIME = EearlyleaveTime;
              }
            } else {
              EARLYLEAVE_TIME = 0;
            }
            if (!EARLYLEAVE_TIME) {
              EARLYLEAVE_TIME = 0;
            }
            EARLYLEAVE_TIME = EARLYLEAVE_TIME;

            // 실제근무시간
            if (item.NEW_UPDATED_START || item.NEW_START_TIME) {
              // 출근 기록이 있는 경우
              REAL_WORKTIME = Math.floor(Number((WORK_TIME ?? 0) - (restTime ?? 0) - LATE_TIME - EARLYLEAVE_TIME));
            } else {
              REAL_WORKTIME = 0;
            }
            // console.log('REAL_WORKTIME22', REAL_WORKTIME);
            if (REAL_WORKTIME <= 0 || today < workDate || !REAL_WORKTIME) {
              // 1: 0보다 작을 경우, 2: 미래일자, 3:
              REAL_WORKTIME = 0;
            }
            // console.log('REAL_WORKTIME33', REAL_WORKTIME);
            REAL_WORKTIME = REAL_WORKTIME;

            // 추가근무시간
            let prevTime = 0; // 기존 일정근무시간
            let nextTime = 0; // 수정된 일정 근무시간
            let realTime = 0; // 실제 근무시간
            let SCS = moment(item.NEW_START).toDate(); // 일정출근
            let SCSM = moment(item.NEW_CHANGE_START).toDate(); // 일정수정출근
            let SCE = moment(item.NEW_END).toDate(); // 일정퇴근
            let SCEM = moment(item.NEW_CHANGE_END).toDate(); //일정수정퇴근
            let QRS = moment(item.NEW_START_TIME).toDate(); // qr출근
            let QRSM = moment(item.NEW_UPDATED_START).toDate(); // qr수정출근
            let QRE = moment(item.NEW_END_TIME).toDate(); // qr퇴근
            let QREM = moment(item.NEW_UPDATED_END).toDate(); // qr수정퇴근
            // console.log('QRS', QRS);
            // console.log('QRSM', QRSM);
            // console.log('QRE', QRE);
            // console.log('QREM', QREM);
            let ST = QRSM; // 수정출근시간
            let ET = QREM; // 수정퇴근시간
            ST == null ? (ST = QRS) : ST; // 수정이 없으면 실제출근시간
            ET == null ? (ET = QRE) : ET; // 수정이 없으면 실제퇴근시간

            if (ST !== null && ST !== undefined && ET !== null && ET !== undefined) {
              // console.log('QRS', QRS);
              // console.log('QRSM', QRSM);
              // console.log('QRE', QRE);
              // console.log('QREM', QREM);
              // console.log('ST', ST);
              // console.log('ET', ET);
              // qr출,퇴근 기록 있음
              prevTime = Math.floor(+new Date(SCE) - +new Date(SCS)) / (60 * 1000);
              // console.log('new Date(SCE)', +new Date(SCE));
              // console.log('+new Date(SCS)', +new Date(SCS));
              // console.log('+new Date(SCE) - +new Date(SCS)', +new Date(SCE) - +new Date(SCS));
              // console.log('prevTime', prevTime);
              if (SCSM !== null && SCSM !== undefined && SCEM !== null && SCEM !== undefined) {
                // 일정출근,퇴근 수정 기록 있음
                nextTime = Math.floor(+new Date(SCEM) - +new Date(SCSM)) / (60 * 1000) ?? 0;
                ST < SCSM ? (ST = SCSM) : ST;
                ET > SCEM ? (ET = SCEM) : ET;
              } else if (SCSM !== null && SCSM !== undefined) {
                // 일정출근 수정 기록있음
                nextTime = Math.floor(+new Date(SCE) - +new Date(SCSM)) / (60 * 1000) ?? 0;
                ST < SCSM ? (ST = SCSM) : ST;
                ET > SCE ? (ET = SCE) : ET;
              } else if (SCEM !== null && SCEM !== undefined) {
                // 일정퇴근 수정 기록있음
                nextTime = Math.floor(+new Date(SCEM) - +new Date(SCS)) / (60 * 1000) ?? 0;
                ST < SCS ? (ST = SCS) : ST;
                ET > SCEM ? (ET = SCEM) : ET;
              } else {
                // 일정수정 기록 없음
                PLUSTIME = 0;
              }
              realTime = Math.floor(+new Date(ET) - +new Date(ST)) / (60 * 1000);
              if (prevTime < nextTime) {
                // 기존일정 근무시간보다 수정된 일정 근무시간이 더 클 경우
                if (prevTime < realTime) {
                  PLUSTIME = realTime - prevTime;
                } else {
                  PLUSTIME = 0;
                }
              } else {
                PLUSTIME = 0;
              }
            } else {
              // qr출,퇴근 기록 없음
              PLUSTIME = 0;
            }
            PLUSTIME = PLUSTIME;

            // 야간근무시간
            NIGHTTIME = 0; // 실제 야간근무시간
            let schStart = item.NEW_CHANGE_START; // 일정출근
            schStart == null ? (schStart = item.NEW_START) : schStart;
            let schEnd = item.NEW_CHANGE_END; // 일정퇴근
            schEnd == null ? (schEnd = item.NEW_END) : schEnd;
            let workStart = item.NEW_UPDATED_START; // 출근시간
            workStart == null ? (workStart = item.NEW_START_TIME) : workStart;
            let workEnd = item.NEW_UPDATED_END; // 퇴근시간
            workEnd == null ? (workEnd = item.NEW_END_TIME) : workEnd;

            if (workStart && schStart && workStart < schStart) workStart = schStart; // 출근시간이 일정보다 작다면 일정출근시간으로 변경
            if (workEnd && schEnd && workEnd > schEnd) workEnd = schEnd; // 퇴근시간이 일정보다 크다면 일정퇴근시간으로 변경
            // console.log('workStart', workStart);
            // console.log('workEnd', workEnd);

            if (workStart && workEnd) {
              // 퇴근시간보다 출근시간이 크면 리턴
              if (new Date(workStart ?? 0) > new Date(workEnd ?? 0)) {
                NIGHTTIME = 0;
              } else {
                // 출퇴근기록이 모두 있을 때
                let startH = new Date(workStart).getHours() * 60; // 출근 비교시간
                let startM = new Date(workStart).getMinutes(); // 출근 비교분
                let endH = new Date(workEnd).getHours() * 60; // 퇴근 비교시간
                let endM = new Date(workEnd).getMinutes(); // 퇴근 비교분
                let startT = startH + startM;
                let endT = endH + endM;
                // 자정을 기준으로 22 ~ 00 , 00 ~ 06 시간 계산
                if (startT > endT) {
                  // console.log('출근시간이 퇴근시간보다 큰 경우 야간근무');
                  // 출근시간이 22시보다 작을 때
                  if (startT < 60 * 22) {
                    // 퇴근시간이 6시보다 작거나 같은 경우
                    if (endT <= 60 * 6) {
                      NIGHTTIME = NIGHTTIME + 60 * 2 + endT;
                    }
                    // 퇴근시간이 6시보다 큰 경우
                    if (endT > 60 * 6) {
                      NIGHTTIME = NIGHTTIME + 60 * 2 + 60 * 6;
                    }
                    // 출근시간이 22시 보다 크거나 같고 24시보다 작거나 같을 때
                  } else if (startT >= 60 * 22 && startT <= 60 * 24) {
                    // 퇴근시간이 6시보다 작거나 같을 경우
                    if (endT <= 60 * 6) {
                      NIGHTTIME = NIGHTTIME + (60 * 24 - startT) + endT;
                    }
                    // 퇴근시간이 6시보다 크고 22시보다 작거나 같은 경우
                    if (endT > 60 * 6 && endT <= 60 * 22) {
                      NIGHTTIME = NIGHTTIME + (60 * 24 - startT) + 60 * 6;
                    }
                    // 퇴근시간이 22시보다 클 경우
                    if (endT > 60 * 22) {
                      NIGHTTIME = NIGHTTIME + (endT - 60 * 22) + (60 * 24 - startT);
                    }
                    // 출근시간이 0시 이상일 때
                  } else if (startT >= 0) {
                    // 퇴근시간이 6시보다 작거나 같을 경우
                    if (endT <= 60 * 6) {
                      NIGHTTIME = NIGHTTIME + endT - startT;
                    }
                    // 퇴근시간이 6시보다 클 경우
                    if (endT > 60 * 6) {
                      NIGHTTIME = NIGHTTIME + (60 * 6 - startT);
                    }
                  } else {
                    NIGHTTIME = NIGHTTIME;
                  }
                } else {
                  // console.log('퇴근시간이 출근시간보다 큰 경우 주간근무');
                  // 출근시간이 6시보다 작을 때
                  if (startT < 60 * 6) {
                    // 퇴근시간이 6시보다 작거나 같은 경우
                    if (endT <= 60 * 6) {
                      NIGHTTIME = NIGHTTIME + endT - startT;
                    }
                    // 퇴근시간이 6시보다 큰 경우
                    if (endT > 60 * 6) {
                      NIGHTTIME = NIGHTTIME + (60 * 6 - startT);
                    }
                    // 출근시간이 6시에서 22시 사이일 경우
                  } else if (60 * 6 <= startT && startT <= 60 * 22) {
                    // 퇴근시간이 22시보다 작을 경우
                    if (endT <= 60 * 22) {
                      NIGHTTIME = NIGHTTIME;
                    }
                    // 퇴근시간이 22시보다 크고 24시 이하일 경우
                    if (endT > 60 * 22 && endT <= 60 * 24) {
                      NIGHTTIME = NIGHTTIME + (endT - 60 * 22);
                    }
                    // 출근시간이 22시 이상일 경우
                  } else if (60 * 22 <= startT) {
                    // 퇴근시간이 24시보다 작거나 같을 경우
                    if (endT <= 60 * 24) {
                      NIGHTTIME = NIGHTTIME + (endT - startT);
                    }
                  } else {
                    NIGHTTIME = NIGHTTIME;
                  }
                }
              }
            } else {
              // 출퇴근 기록이 모두 있지 않으면
              NIGHTTIME = 0;
            }
            NIGHTTIME = NIGHTTIME;

            WORK_ON_MODEL = item.WORK_MODEL;
            WORK_OFF_MODEL = item.WORK_OFF_MODEL;
          }

          // 근무상태
          let workType;
          if (!item) {
            workType = '';
          } else {
            // 일정 있을 경우
            if (item.VACATION == '1') {
              workType = '휴무';
            } else {
              if (today < workDate) {
                // 미래
                workType = '출근예정';
              } else {
                // 오늘과 과거
                if (!item.NEW_START_TIME && !item.NEW_UPDATED_START) {
                  workType = '결근';
                } else {
                  if (item.NEW_END_TIME || item.NEW_UPDATED_END) {
                    workType = '퇴근';
                  } else {
                    workType = '근무중';
                  }
                }
              }
            }
          }
          SCH_STATUS = workType;
          // 근무유형
          SCH_TYPE = item.TYPE == '4' ? '자율출퇴근' : item.TYPE == '3' ? '추가일정' : '일정근무';
          SCH_ID = Number(item.SCH_ID ? item.SCH_ID : 0);
          EMP_ID = Number(item.EMP_ID ?? 0);
          if (item.TYPE == '4') {
            NEW_START = null;
            NEW_END = null;
            NEW_CHANGE_START = null;
            NEW_CHANGE_END = null;
          }

          let form = {
            MEMBER_SEQ: item.MEMBER_SEQ,
            DATE: moment(DATE).format('YYYY년MM월DD일'), // 일자ㅇ
            WEEKEND: WEEKEND, // 요일ㅇ
            NAME: NAME, // 직원명ㅇ
            NEW_START: NEW_START, // 일정출근d
            NEW_END: NEW_END, // 일정퇴근d
            NEW_CHANGE_START: NEW_CHANGE_START, // 일정수정출근d
            NEW_CHANGE_END: NEW_CHANGE_END, // 일정수정퇴근d
            NEW_START_TIME: NEW_START_TIME, // qr출근d
            NEW_END_TIME: NEW_END_TIME, // qr퇴근d
            NEW_UPDATED_START: NEW_UPDATED_START, // qr수정출근d
            NEW_UPDATED_END: NEW_UPDATED_END, // qr수정퇴근d
            WORK_TIME: WORK_TIME && typeof WORK_TIME === 'number' ? formatTimeNumberToKorean(WORK_TIME) : null, // 예정근무시간
            REST_TIME: REST_TIME && typeof REST_TIME === 'number' ? formatTimeNumberToKorean(REST_TIME) : null, // 휴게시간
            LATE_TIME: LATE_TIME && typeof LATE_TIME === 'number' ? formatTimeNumberToKorean(LATE_TIME) : null, // 지각
            EARLYLEAVE_TIME:
              EARLYLEAVE_TIME && typeof EARLYLEAVE_TIME === 'number' ? formatTimeNumberToKorean(EARLYLEAVE_TIME) : null, // 조퇴
            REAL_WORKTIME:
              REAL_WORKTIME && typeof REAL_WORKTIME === 'number' ? formatTimeNumberToKorean(REAL_WORKTIME) : null, // 실제근무시간
            PLUSTIME: PLUSTIME && typeof PLUSTIME === 'number' ? formatTimeNumberToKorean(PLUSTIME) : null, // 추가근무시간
            NIGHTTIME: NIGHTTIME && typeof NIGHTTIME === 'number' ? formatTimeNumberToKorean(NIGHTTIME) : null, // 야간근무시간
            SCH_STATUS: SCH_STATUS, // 근무상태
            SCH_TYPE: SCH_TYPE, // 근무유형
            SCH_ID: SCH_ID,
            EMP_ID: EMP_ID, // 직원 seq
            PAYTYPE: item.paytype, // 급여유형
            STATUS: item.STATUS ? item.STATUS : '6', // 수당포함여부 값
            WORK_ON_MODEL, // 출근기기
            WORK_OFF_MODEL, // 퇴근기기
          };
          formatData.push(form);
          WORK_TIME = WORK_TIME ? WORK_TIME : 0;
          REST_TIME = REST_TIME ? REST_TIME : 0;
          LATE_TIME = LATE_TIME ? LATE_TIME : 0;
          EARLYLEAVE_TIME = EARLYLEAVE_TIME ? EARLYLEAVE_TIME : 0;
          REAL_WORKTIME = REAL_WORKTIME ? REAL_WORKTIME : 0;
          PLUSTIME = PLUSTIME ? PLUSTIME : 0;
          NIGHTTIME = NIGHTTIME ? NIGHTTIME : 0;
          totalFormat[0].DATE = '합계';
          if (typeof WORK_TIME === 'number' && SCH_TYPE !== '자율출퇴근')
            totalFormat[0].WORK_TIME = totalFormat[0].WORK_TIME ? totalFormat[0].WORK_TIME + WORK_TIME : WORK_TIME;

          if (typeof REST_TIME === 'number')
            totalFormat[0].REST_TIME = totalFormat[0].REST_TIME ? totalFormat[0].REST_TIME + REST_TIME : REST_TIME;

          if (typeof LATE_TIME === 'number')
            totalFormat[0].LATE_TIME = totalFormat[0].LATE_TIME ? totalFormat[0].LATE_TIME + LATE_TIME : LATE_TIME;

          if (typeof EARLYLEAVE_TIME === 'number')
            totalFormat[0].EARLYLEAVE_TIME = totalFormat[0].EARLYLEAVE_TIME
              ? totalFormat[0].EARLYLEAVE_TIME + EARLYLEAVE_TIME
              : EARLYLEAVE_TIME;

          if (typeof REAL_WORKTIME === 'number')
            totalFormat[0].REAL_WORKTIME = totalFormat[0].REAL_WORKTIME
              ? totalFormat[0].REAL_WORKTIME + REAL_WORKTIME
              : REAL_WORKTIME;

          if (typeof PLUSTIME === 'number')
            totalFormat[0].PLUSTIME = totalFormat[0].PLUSTIME ? totalFormat[0].PLUSTIME + PLUSTIME : PLUSTIME;

          if (typeof NIGHTTIME === 'number')
            totalFormat[0].NIGHTTIME = totalFormat[0].NIGHTTIME ? totalFormat[0].NIGHTTIME + NIGHTTIME : NIGHTTIME;
        });
      } else {
        let form: ICommuteItem = {
          MEMBER_SEQ: 0,
          DATE: moment(key).format('YYYY년MM월DD일'), // 일자
          WEEKEND: getDayWithKorean(moment(key).toDate()), // 요일
          NAME: '', // 직원명
          NEW_START: null, // 일정출근
          NEW_END: null, // 일정퇴근
          NEW_CHANGE_START: null, // 일정수정출근
          NEW_CHANGE_END: null, // 일정수정퇴근
          NEW_START_TIME: null, // qr출근
          NEW_END_TIME: null, // qr퇴근
          NEW_UPDATED_START: null, // qr수정출근
          NEW_UPDATED_END: null, // qr수정퇴근
          WORK_TIME: null, // 예정근무시간
          REST_TIME: null, // 휴게시간
          LATE_TIME: null, // 지각
          EARLYLEAVE_TIME: null, // 조퇴
          REAL_WORKTIME: null, // 실제근무시간
          PLUSTIME: null, // 추가근무시간
          NIGHTTIME: null, // 야간근무시간
          SCH_STATUS: '', // 근무상태
          SCH_TYPE: '무일정', // 근무유형
          SCH_ID: 0,
          EMP_ID: 0,
          PAYTYPE: '0',
          STATUS: '6',
          WORK_ON_MODEL: null, // 출근기기
          WORK_OFF_MODEL: null, // 퇴근기기
        };
        formatData.push(form);
      }
    }

    setCommuteData(formatData);
    setCommuteTotalData([
      {
        ...totalFormat[0],
        WORK_TIME: formatTimeNumberToKorean(totalFormat[0]?.WORK_TIME),
        REST_TIME: formatTimeNumberToKorean(totalFormat[0]?.REST_TIME),
        LATE_TIME: formatTimeNumberToKorean(totalFormat[0]?.LATE_TIME),
        EARLYLEAVE_TIME: formatTimeNumberToKorean(totalFormat[0]?.EARLYLEAVE_TIME),
        REAL_WORKTIME: formatTimeNumberToKorean(totalFormat[0]?.REAL_WORKTIME),
        PLUSTIME: formatTimeNumberToKorean(totalFormat[0]?.PLUSTIME),
        NIGHTTIME: formatTimeNumberToKorean(totalFormat[0]?.NIGHTTIME),
      },
    ]);

    setLoadingState({ isFetchLoading: false });
  };

  // 추가일정등록 버튼 클릭
  const toggleModal = () => {
    if (userType === '0' && managerInfo.calendarEdit === false) {
      return setModalState({
        isModalShow: true,
        title: '권한 없음',
        desc: `추가일정등록 권한은 사업주님이 부여하실 수 있습니다.`,
      });
    }
    if (!scheduleRegModal && empSelectList.length === 1) {
      return setModalState({
        isModalShow: true,
        title: '추가일정등록',
        desc: `등록가능한 직원이 없습니다`,
      });
    }
    setScheduleRegModal(!scheduleRegModal);
  };

  // 추가일정 등록하기
  const submitAddSchedule = async (
    emps: IScheEmpItem[],
    startTime: string,
    endTime: string,
    dates: ISchDateItem[],
    allowance: string,
  ) => {
    const formatEmps = emps.map((i) => ({
      EMP_SEQ: i.value,
      EMP_NAME: i.label,
    }));
    const formatDates = dates.map((d) => moment(d.date).format('YYYY-MM-DD'));

    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiPhp('post', `/Management/new_schedule_create`, {
        EMPS: formatEmps,
        DATE: formatDates,
        SCHEDULETYPE: allowance,
        TYPE: '3',
        START: startTime,
        END: endTime,
        COLOR: '#0D4F8A',
        STORE_NAME: storeName,
        STORE_ID: storeSeqNumber,
      });
      console.log('/Management/new_schedule_create_data', data);

      if (data.message === 'SUCCESS') {
        fetchSchedules();
        toggleModal();
        setModalState({
          isModalShow: true,
          title: '추가일정등록',
          desc: `완료되었습니다`,
        });
      } else if (data.message === 'ALREADY_SUCCESS') {
        setModalState({
          isModalShow: true,
          title: '추가일정등록',
          desc: `이미 일정이 등록되어 있습니다`,
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /Management/new_schedule_create');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 테이블 excel, pdf 다운로드 할 때 이름 생성하여 리턴
  const getFileName = React.useCallback(() => {
    let selectEmp = selectedEmp?.empName;
    let selectDate = `${moment(startDate).format('YYYY년MM월DD일')}~${moment(endDate).format('YYYY년MM월DD일')}`;
    // console.log('selectDate', selectDate);
    return `${selectEmp}_출퇴근기록(${selectDate})`;
  }, [selectedEmp?.empName, startDate, endDate]);

  // table excel다운로드
  const excelDown = () => {
    // if (gridExcelExportRef.current) {
    //   gridExcelExportRef.current.save();
    // }
    if (AgGridRef.current) {
      AgGridRef.current.api.exportDataAsExcel({
        fileName: getFileName(),
        sheetName: '샵솔',
        headerRowHeight: 30,
        autoConvertFormulas: true,
      });
    }
  };
  // table pdf다운로드
  const pdfDown = () => {
    // if (gridPdfExportRef.current) {
    //   gridPdfExportRef.current.save();
    // }
    if (AgGridRef.current) {
      exportToPdf(AgGridRef.current, getFileName());
    }
  };

  // 출퇴근 기록 수정 모달 토글
  const toggleCommuteModifyModal = () => {
    setVisibleCommuteModifyModal(!visibleCommuteModifyModal);
  };

  // 테이블 로우 클릭 시
  const clickCommuteItem = (e: RowClickedEvent) => {
    if (e.data.SCH_TYPE == '무일정') {
      return setModalState({
        isModalShow: true,
        title: '출퇴근기록',
        desc: `등록된 일정이 없습니다. '+추가일정 등록' 버튼을 클릭하여 등록 가능합니다`,
      });
    } else {
      if (userType === '0' && managerInfo.calendarEdit === false) {
        return setModalState({
          isModalShow: true,
          title: '권한 없음',
          desc: `출퇴근기록 수정 권한은 사업주님이 부여하실 수 있습니다.`,
        });
      }
      console.log('@@44');

      setModalState({
        isModalShow: true,
        title: '근무일정 수정',
        desc: '출퇴근 기록 근무일정을 수정하여도 기존 근무일정대로 주휴수당이 계산됩니다. \n\n만약 수정한 근무일정대로 주휴수당을 변경하려면 직원목록 > 정보수정 > 근무일정에서 근무일정을 수정해주세요.',
        okBtnText: '확인',
        okCallback: () => {
          let formatData = { ...e.data };
          formatData.viewType = 'modify';

          setSelectedCommuteRow(formatData);
          toggleCommuteModifyModal();
        },
        isWarning: false,
      });
      // 타임피커 사용 데이터만 날짜 객체로 가공
    }
  };

  const modifyCommuteItem = async (item: IStateModal, SCH_ID: number) => {
    try {
      setLoadingState({ isFetchLoading: true });
      let DATE = item.DATE.replace('년', '-').replace('월', '-').replace('일', '');
      let start = `${DATE} ${item.START.replace('시', ':').replace('분', '')}:00`;
      let startM = item.START_M;
      let endM = item.END_M;
      let end = `${DATE} ${item.END.replace('시', ':').replace('분', '')}:00`;
      let QRstartM = item.QRSTART_M;
      let QRendM = item.QREND_M;
      // 일정수정, 출퇴근시간 수정, 휴게시간의 값이 모두 없는 경우
      // if (startM == null && endM == null && QRstartM == null && QRendM == null && item.RESTTIME === 0) {
      //   return setModalState({
      //     isModalShow: true,
      //     title: '출퇴근기록 수정',
      //     desc: `수정할 시간을 입력해 주세요`,
      //   });
      // }
      // 일정 수정 중 출근,퇴근 한가지만 입력한 경우
      if ((startM !== null && endM == null) || (endM !== null && startM == null)) {
        return setModalState({
          isModalShow: true,
          title: '출퇴근기록 수정',
          desc: `수정 - 일정출근,퇴근을 모두 설정해 주세요`,
        });
      }
      // 일정출,퇴근 수정 시간이 동일한 경우
      if (startM !== null && endM !== null && startM == endM) {
        return setModalState({
          isModalShow: true,
          title: '출퇴근기록 수정',
          desc: `수정 - 일정출근,퇴근시간이 동일합니다. 올바른 시간을 입력해 주세요`,
        });
      }
      // 출퇴근시간 수정 중 출근시간,퇴근시간 한가지만 입력한 경우
      if ((QRstartM !== null && QRendM == null) || (QRendM !== null && QRstartM == null)) {
        return setModalState({
          isModalShow: true,
          title: '출퇴근기록 수정',
          desc: `수정 - 출근,퇴근시간을 모두 설정해 주세요`,
        });
      }
      // 출,퇴근 수정 시간이 동일한 경우
      if (QRstartM !== null && QRendM !== null && QRstartM == QRendM) {
        return setModalState({
          isModalShow: true,
          title: '출퇴근기록 수정',
          desc: `수정 - 출근,퇴근시간이 동일합니다. 올바른 시간을 입력해 주세요`,
        });
      }
      // 일정시간을 수정할 때 퇴근시간이 출근시간보다 빠른경우 (야간근무)
      if (startM && endM && moment(DATE + ' ' + startM).isAfter(DATE + ' ' + endM)) {
        endM = moment(DATE + ' ' + endM)
          .add(1, 'day')
          .toDate();
        console.log('endM', endM);
      }
      // 출,퇴근시간을 수정할 때 퇴근시간이 출근시간보다 빠른경우 (야간근무)
      if (QRstartM && QRendM && moment(DATE + ' ' + QRstartM).isAfter(DATE + ' ' + QRendM)) {
        QRendM = moment(DATE + ' ' + QRendM)
          .add(1, 'day')
          .toDate();
        console.log('QRendM', QRendM);
      }
      if (isNaN(Number(item.RESTTIME))) {
        if (restTimeInput.current) restTimeInput.current?.focus();
        return setModalState({
          isModalShow: true,
          title: '출퇴근기록 수정',
          desc: `올바른 휴게시간을 분단위로 입력해 주세요`,
        });
      }

      if (SCH_ID !== 0) {
        const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/setSchedule`, {
          EMP_SEQ: item.EMP_ID,
          DATE: DATE,
          NEW_CHANGE_START: startM ? moment(`${DATE} ${startM}`).format('YYYY-MM-DD HH:mm:ss') : null,
          NEW_CHANGE_END: moment(endM).isValid()
            ? moment(endM).format('YYYY-MM-DD HH:mm:ss')
            : endM
            ? moment(`${DATE} ${endM}`).format('YYYY-MM-DD HH:mm:ss')
            : null,
          NEW_UPDATED_START: QRstartM ? moment(`${DATE} ${QRstartM}`).format('YYYY-MM-DD HH:mm:ss') : null,
          NEW_UPDATED_END: moment(QRendM).isValid()
            ? moment(QRendM).format('YYYY-MM-DD HH:mm:ss')
            : QRendM
            ? moment(`${DATE} ${QRendM}`).format('YYYY-MM-DD HH:mm:ss')
            : null,
          REST_TIME: item.RESTTIME,
          incentiveCheck: item.STATUS,
          MEMBER_SEQ: item.MEMBER_SEQ,
        });
        console.log('/auth/setSchedule_data', data);

        if (data.resultcode === 0) {
          toggleCommuteModifyModal();
          setModalState({
            isModalShow: true,
            title: '출퇴근기록 수정',
            desc: `완료되었습니다`,
          });
          fetchSchedules();
        } else {
          throw new Error(data.resultMsg);
        }
      } else {
        const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/insertSchedule`, {
          EMP_SEQ: item.EMP_ID,
          DATE: DATE,
          NEW_START: start,
          NEW_END: end,
          NEW_CHANGE_START: startM ? moment(`${DATE} ${startM}`).format('YYYY-MM-DD HH:mm:ss') : null,
          NEW_CHANGE_END: moment(endM).isValid()
            ? moment(endM).format('YYYY-MM-DD HH:mm:ss')
            : endM
            ? moment(`${DATE} ${endM}`).format('YYYY-MM-DD HH:mm:ss')
            : null,
          NEW_UPDATED_START: QRstartM ? moment(`${DATE} ${QRstartM}`).format('YYYY-MM-DD HH:mm:ss') : null,
          NEW_UPDATED_END: moment(QRendM).isValid()
            ? moment(QRendM).format('YYYY-MM-DD HH:mm:ss')
            : QRendM
            ? moment(`${DATE} ${QRendM}`).format('YYYY-MM-DD HH:mm:ss')
            : null,
          REST_TIME: item.RESTTIME,
          incentiveCheck: item.STATUS,
          MEMBER_SEQ: item.MEMBER_SEQ,
        });
        console.log('/auth/insertSchedule_data', data);

        if (data.resultcode === 0) {
          toggleCommuteModifyModal();
          setModalState({
            isModalShow: true,
            title: '출퇴근기록 수정',
            desc: `완료되었습니다`,
          });
          fetchSchedules();
        } else {
          throw new Error(data.resultMsg);
        }
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/setSchedule');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };
  // 추가일정 삭제
  const addSchDelete = async (SCH_ID: number) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/deleteschedule`, {
        SCH_ID,
      });
      console.log('/auth/deleteschedule_data', data);

      if (data.resultCode === 0) {
        toggleCommuteModifyModal();
        setModalState({
          isModalShow: true,
          title: '추가일정삭제',
          desc: `완료되었습니다`,
        });
        fetchSchedules();
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/deleteschedule');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };
  const getAnnualData = async (EMP_SEQ: number, date: string) => {
    try {
      if (EMP_SEQ === 0) return;
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiPhp(
        'get',
        `/Employee/getEmpAnnual?EMP_SEQ=${EMP_SEQ}&YEAR=${moment(onlyNumberInput(date)).year()}`,
      );
      // console.log('/Employee/getEmpAnnual_data', data);

      if (data.message.length !== 0) {
        setSelectedCommuteRow((prevState) => {
          if (prevState) {
            return {
              ...prevState,
              viewType: 'annual',
              ANNUAL: data.message[0].ANNUAL,
              USE_ANNUAL: data.message[0].USE_ANNUAL,
            };
          }
        });
      } else {
        setSelectedCommuteRow((prevState) => {
          if (prevState) {
            return { ...prevState, viewType: 'annual', ANNUAL: 0, USE_ANNUAL: 0 };
          }
        });
      }
    } catch (err) {
      checkErrorLog(err, ' /Employee/getEmpAnnual');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 수정모드로 돌아가기
  const backModifyModal = () => {
    setSelectedCommuteRow((prevState) => {
      if (prevState) {
        return {
          ...prevState,
          viewType: 'modify',
          ANNUAL: 0,
          USE_ANNUAL: 0,
        };
      }
    });
  };

  // 휴무 적용 (유급or무급)
  const setHoliday = async (holidayType: '유급' | '무급', weekAllowanceType?: string) => {
    try {
      if (holidayType == '유급') {
        if (!(Number(selectedCommuteRow?.ANNUAL) - Number(selectedCommuteRow?.USE_ANNUAL) > 0)) {
          return setModalState({
            isModalShow: true,
            title: '휴무설정',
            desc: `사용가능한 연차가 없습니다. 무급휴무를 적용해 주세요`,
          });
        }
      }
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiPhp('post', `/Management/createScheduleVacation2`, {
        EMP_SEQ: selectedCommuteRow?.EMP_ID,
        STORE_ID: storeSeqNumber,
        EMP_NAME: selectedCommuteRow?.NAME,
        DATE: moment(
          typeof selectedCommuteRow?.DATE === 'string'
            ? selectedCommuteRow?.DATE.toString().replace(/[^0-9]/g, '')
            : selectedCommuteRow?.DATE,
        ).format('YYYY-MM-DD'),
        TYPE: holidayType === '유급' ? '1' : '0',
        weekChecks: weekAllowanceType,
      });
      // console.log('/Management/createScheduleVacation2_data', data);

      if (data.message == 'SUCCESS') {
        fetchSchedules();
        toggleCommuteModifyModal();
        setModalState({
          isModalShow: true,
          title: '휴무설정',
          desc: `${holidayType} 휴무가 적용되었습니다`,
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /Management/createScheduleVacation2');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };
  const cancelHoliday = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiPhp('post', `/Management/cancelScheduleVacation`, {
        SCH_ID: selectedCommuteRow?.SCH_ID,
      });
      // console.log('/Management/cancelScheduleVacation_data', data);

      if (data.message === 'SUCCESS') {
        fetchSchedules();
        toggleCommuteModifyModal();
        setModalState({
          isModalShow: true,
          title: '휴무취소',
          desc: `휴무가 취소되었습니다`,
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /Management/cancelScheduleVacation');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const RenderHeader = (title: string) => {
    let Desc = '';
    if (title == '일정출근') Desc = '관리자가 근무자에게 부여한 근무의 기준 출근시간';
    if (title == '일정퇴근') Desc = '관리자가 근무자에게 부여한 근무의 기준 퇴근시간';
    if (title == '수정-일정출근') Desc = '관리자가 수정한 일정 출근시간';
    if (title == '수정-일정퇴근') Desc = '관리자가 수정한 일정 퇴근시간';
    if (title == '실제 출근시간') Desc = '근무자가 실제로 출근을 진행한 시간';
    if (title == '실제 퇴근시간') Desc = '근무자가 실제로 퇴근을 진행한 시간';
    if (title == '수정-실제 출근시간') Desc = '관리자가 수정한 근무자의 출근 시간';
    if (title == '수정-실제 퇴근시간') Desc = '관리자가 수정한 근무자의 퇴근 시간';
    if (title == '예정근무시간') Desc = '일정으로 정해진 출근시간부터 퇴근시간까지의 시간';
    if (title == '휴게시간')
      Desc = '직원의 급여유형이 "시급"이고 "휴게시간 차감 설정"에서 입력한 시간 (직원정보에서 변경할 수 있습니다.)';
    if (title == '지각') Desc = '일정출근 시간을 기준으로 출근시간이 늦을 경우 발생하는 시간';
    if (title == '조퇴') Desc = '일정퇴근 시간을 기준으로 퇴근시간이 빠를 경우 발생하는 시간';
    if (title == '실제 근무시간') Desc = '출/퇴근 시간에서 지각, 조퇴, 휴게시간을 차감하여 산정한 근무시간';
    if (title == '추가근무시간')
      Desc = '기존 일정시간을 수정하여 실제근무시간이 기존 일정 보다 많을 경우 기입되는 시간';
    if (title == '야간근무시간') Desc = '실제근무시간 중 22시부터 06시 사이의 시간';
    return Desc;
  };

  const [columns, setColumns] = React.useState<(ColDef | ColGroupDef)[]>([
    {
      field: 'DATE',
      headerName: '일자',
      pinned: 'left',
      width: 150,
    },
    {
      field: 'WEEKEND',
      headerName: '요일',
      pinned: 'left',
      width: 80,
    },
    {
      field: 'NAME',
      headerName: '직원명',
      pinned: 'left',
      width: 100,
    },
    {
      field: 'SCH_TYPE',
      headerName: '근무유형',
      cellRenderer: ({ data }: { data: ICommuteItem }) => {
        if (data?.SCH_TYPE) {
          if (data.SCH_TYPE?.indexOf('추가일정') !== -1) {
            return <div>{data.SCH_TYPE.substr(0, 4)}</div>;
          } else {
            return <div>{data.SCH_TYPE}</div>;
          }
        } else {
          return null;
        }
      },
    },
    {
      field: 'SCH_STATUS',
      headerName: '근무상태',
    },
    {
      field: 'NEW_START',
      headerName: '일정출근',
      headerTooltip: RenderHeader('일정출근'),
    },
    {
      field: 'NEW_END',
      headerName: '일정퇴근',
      headerTooltip: RenderHeader('일정퇴근'),
    },
    {
      field: 'NEW_CHANGE_START',
      headerName: '수정-일정출근',
      headerTooltip: RenderHeader('수정-일정출근'),
    },
    {
      field: 'NEW_CHANGE_END',
      headerName: '수정-일정퇴근',
      headerTooltip: RenderHeader('수정-일정출근'),
    },
    {
      field: 'NEW_START_TIME',
      headerName: '실제 출근시간',
      headerTooltip: RenderHeader('실제 출근시간'),
    },
    {
      field: 'NEW_END_TIME',
      headerName: '실제 퇴근시간',
      headerTooltip: RenderHeader('실제 퇴근시간'),
    },
    {
      field: 'NEW_UPDATED_START',
      headerName: '수정-실제 출근시간',
      headerTooltip: RenderHeader('수정-실제 출근시간'),
    },
    {
      field: 'NEW_UPDATED_END',
      headerName: '수정-실제 퇴근시간',
      headerTooltip: RenderHeader('수정-실제 퇴근시간'),
    },
    {
      field: 'WORK_TIME',
      headerName: '예정근무시간',
      headerTooltip: RenderHeader('예정근무시간'),
      valueFormatter: (props) => {
        if (props.data?.SCH_TYPE === '자율출퇴근') return '자율출퇴근';
        return props.value;
      },
    },
    {
      field: 'REST_TIME',
      headerName: '휴게시간',
      headerTooltip: RenderHeader('휴게시간'),
    },
    {
      field: 'LATE_TIME',
      headerName: '지각',
      headerTooltip: RenderHeader('지각'),
    },
    {
      field: 'EARLYLEAVE_TIME',
      headerName: '조퇴',
      headerTooltip: RenderHeader('조퇴'),
    },
    {
      field: 'REAL_WORKTIME',
      headerName: '실제 근무시간',
      headerTooltip: RenderHeader('실제 근무시간'),
    },
    {
      field: 'PLUSTIME',
      headerName: '추가근무시간',
      headerTooltip: RenderHeader('추가근무시간'),
    },
    {
      field: 'NIGHTTIME',
      headerName: '야간근무시간',
      headerTooltip: RenderHeader('야간근무시간'),
    },
    {
      field: 'WORK_ON_MODEL',
      headerName: '출근기기',
      headerTooltip: RenderHeader('출근기기'),
    },
    {
      field: 'WORK_OFF_MODEL',
      headerName: '퇴근기기',
      headerTooltip: RenderHeader('퇴근기기'),
    },
  ]);

  const defaultColDef = React.useMemo<ColDef>(() => {
    return {
      filter: true,
      filterParams: {
        button: ['reset'],
        closeOnApply: true,
      },
      cellStyle: {
        textAlign: 'center',
      },
    };
  }, []);

  React.useEffect(() => {
    fetchEmpByPeriodData();
  }, [startDate, endDate]);

  React.useEffect(() => {
    if (commuteData.length === 0) {
      fetchSchedules();
    }
  }, [selectedEmp]);

  const CustomOption = (
    props: OptionProps<{
      value: number | undefined;
      label: string | undefined;
      retireDate: Date | null;
    }>,
  ) => {
    return (
      <components.Option {...props}>
        <div>
          {props.children}
          {props.data.retireDate && (
            <div>
              <span style={{ fontSize: '0.8rem', color: '#777' }}>
                퇴사일 {moment(props.data.retireDate).format('YYYY.MM.DD')}
              </span>
            </div>
          )}
        </div>
      </components.Option>
    );
  };

  const gridExcelExportRef = React.useRef<ExcelExport | null>(null);
  const gridPdfExportRef = React.useRef<GridPDFExport | null>(null);

  const empSelectList = selectEmpList.map((i) => ({
    value: i.empSeq ?? '',
    label: i.empName ?? '',
    retireDate: i.retireDate,
  }));

  const formatSelectEmpOption = {
    value: selectedEmp?.empSeq,
    label: selectedEmp?.empName,
    retireDate: selectedEmp?.retireDate ?? null,
  };

  const AgGridRef = React.useRef<AgGridReact>(null);
  const Allowance1 = React.useRef<HTMLInputElement>(null);
  const Allowance2 = React.useRef<HTMLInputElement>(null);
  const Allowance3 = React.useRef<HTMLInputElement>(null);
  const Allowance4 = React.useRef<HTMLInputElement>(null);
  const restTimeInput = React.useRef<HTMLInputElement>(null);

  return (
    <>
      <WithSideBarAndNavBar>
        <div className="commuting">
          <div className="commuting__head">출퇴근 기록</div>
          <div className="commuting__buttons">
            <div className="commuting__buttons__calendar">
              <DateRangeSelector
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
            </div>
            <div>
              <Select
                className="payroll-select__dropdown"
                components={{
                  IndicatorSeparator: () => null,
                  DropdownIndicator: () => (
                    <div style={{ paddingRight: '15px' }}>
                      <ArrowDownIcon />
                    </div>
                  ),
                  Control: ({ children, ...props }: any) => {
                    return (
                      <components.Control {...props}>
                        <div
                          style={{
                            paddingLeft: '10px',
                            fontFamily: 'Spoqa Han Sans Neo',
                            fontWeight: '500',
                            fontSize: '16px',
                            color: '#868686',
                          }}>
                          직원명
                        </div>
                        {children}
                      </components.Control>
                    );
                  },
                  Option: CustomOption,
                }}
                noOptionsMessage={() => '해당 기간 근무 직원 없음'}
                isSearchable={true}
                placeholder="직원선택"
                styles={colourStyles}
                value={formatSelectEmpOption}
                options={empSelectList}
                isMulti={false}
                onChange={handleSelectEmp}
              />
            </div>
            <div className="commuting__buttons__inquiry">
              <Button label="조회" type="type2" height={38} action={fetchSchedules} />
            </div>
            <div className="commuting__buttons__pdf">
              <Button label="PDF 다운" type="type8" height={38} action={pdfDown} />
            </div>
            <div className="commuting__buttons__excel">
              <Button label="Excel 다운" type="type8" height={38} action={excelDown} />
            </div>
            <div className="commuting__buttons__schedule">
              <Button label="+ 추가일정등록" type="type5" height={38} action={toggleModal} />
            </div>
          </div>

          <div className="commuting__text" id="second-app-title" style={{ display: 'flex', flexDirection: 'column' }}>
            <strong>- 시작일~종료일을 각각 설정하시면, 해당기간에 근무한 직원들이 선택목록에 표시됩니다</strong>
            <strong>- 선택목록에서 직원을 선택한 후 "조회"버튼을 눌러주세요</strong>
          </div>

          <div className="gridArea">
            <AgGrid
              AgGridRef={AgGridRef}
              rowData={commuteData}
              colDefs={columns}
              defaultColDef={defaultColDef}
              pinnedBottomRowData={commuteTotalData}
              onClickRow={clickCommuteItem}
            />
          </div>
          {/* <div className="gridArea">
            <CommuteGrid
              data={commuteData}
              selectEmpList={selectEmpList}
              gridExcelExportRef={gridExcelExportRef}
              gridPdfExportRef={gridPdfExportRef}
              clickCommuteItem={clickCommuteItem}
              getFileName={getFileName}
            />
          </div> */}

          <div className="commuting__footer-text">
            <div className="commuting__footer-text__head">아래 사항을 참고해 주세요!</div>
            <div className="commuting__footer-text__body">
              - 근무자가 출근한 뒤 20시간 동안 퇴근하지 않으면 자동으로 퇴근 처리되며 '퇴근미체크'로 표기됩니다.
            </div>
            <div className="commuting__footer-text__body">
              - 테이블을 선택하면 해당 일정의 수정, 삭제(추가일정), 휴무처리를 할 수 있습니다.
            </div>
          </div>
        </div>
      </WithSideBarAndNavBar>
      <CommonModal isVisible={scheduleRegModal} onClose={toggleModal} title={'추가일정등록'}>
        <ScheduleRegModal selectEmpList={selectEmpList} submitAddSchedule={submitAddSchedule} />
      </CommonModal>
      <CommonModal
        isVisible={visibleCommuteModifyModal}
        onClose={toggleCommuteModifyModal}
        title={selectedCommuteRow?.viewType === 'modify' ? '출퇴근기록 수정' : '휴무설정'}>
        <CommuteModify
          modalState={selectedCommuteRow ?? null}
          onClose={toggleCommuteModifyModal}
          Allowance1={Allowance1}
          Allowance2={Allowance2}
          Allowance3={Allowance3}
          Allowance4={Allowance4}
          restTimeInput={restTimeInput}
          modifyCommuteItem={modifyCommuteItem}
          addSchDelete={addSchDelete}
          getAnnualData={getAnnualData}
          backModifyModal={backModifyModal}
          setHoliday={setHoliday}
          cancelHoliday={cancelHoliday}
        />
      </CommonModal>
    </>
  );
};
export default Commuting;
